import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { esES } from "@mui/material/locale";
import MaterialTable from "@material-table/core";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import Modal from "../../Modal/Modal";
import { ContractApprove } from "../ContractApprove/ContractApprove";
import { currencyFormatter } from "../../../helpers/currencyHelper";

export function ContractsList({
  contracts,
  title,
  pageSize,
  reload,
  setReload,
  handleRefreshChange,
  autoOpenContractId,
}) {
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [modalContract, setModalContract] = useState(null);

  // Coming from a notification click (e.g. "PARC 360 contract ready for
  // approval") - open that specific contract's Approve modal as soon as it
  // shows up in the list, instead of making the user find it themselves.
  useEffect(() => {
    if (!autoOpenContractId) {
      return;
    }
    const match = (contracts || []).find((c) => c._id === autoOpenContractId);
    if (match) {
      setModalContract(match);
      setIsVisibleModal(true);
    }
  }, [autoOpenContractId, contracts]);

  const theme = createTheme(
    { palette: { primary: { main: "#1976d2" } } },
    esES,
  );

  const fmtDate = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
  const fmtAmount = (n) => currencyFormatter(Number(n ?? 0));

  const getPlanNames = (rowData) => {
    const names = (rowData?.relatedSites || [])
      .map((s) => s?.selectedPARC?.name)
      .filter(Boolean);
    return Array.from(new Set(names)).join(", ") || "-";
  };

  const openApprove = (rowData) => {
    setModalContract(rowData);
    setIsVisibleModal(true);
  };

  const columns = [
    {
      title: "Portfolio Account",
      field: "account.accountName",
      render: (rowData) => (
        <span className="table-entity">
          <span className="table-entity__icon">
            <BusinessOutlinedIcon />
          </span>
          <span className="table-entity__text">
            {rowData?.account?.accountName || "-"}
          </span>
        </span>
      ),
    },
    { title: "Plan", render: getPlanNames },
    {
      title: "Sites",
      render: (rowData) => (rowData?.relatedSites || []).length,
    },
    {
      title: "Total Amount",
      field: "totalAmount",
      render: (rowData) => fmtAmount(rowData?.totalAmount),
    },
    {
      title: "Effective Date",
      field: "effectiveDate",
      render: (rowData) => fmtDate(rowData?.effectiveDate),
    },
    {
      title: "Expiration Date",
      field: "expirationDate",
      render: (rowData) => fmtDate(rowData?.expirationDate),
    },
  ];

  return (
    <>
      <div className="listContainer">
        <ThemeProvider theme={theme}>
          <MaterialTable
            columns={columns}
            className="genericTable"
            data={contracts}
            actions={[
              {
                icon: () => (
                  <CheckCircleOutlineIcon className="table-action-icon table-action-icon--success" />
                ),
                tooltip: "Review and Approve",
                onClick: (_e, rowData) => openApprove(rowData),
              },
            ]}
            options={{
              actionsColumnIndex: -1,
              pageSize: pageSize || 20,
              filtering: false,
              headerStyle: {
                position: "sticky",
                top: 0,
                zIndex: 100,
                backgroundColor: "white",
              },
            }}
            style={{ width: "100%" }}
            title={title}
          />
        </ThemeProvider>
      </div>

      <Modal
        title="Approve PARC 360 Contract"
        open={isVisibleModal}
        setIsVisible={setIsVisibleModal}
      >
        {modalContract && (
          <ContractApprove
            contract={modalContract}
            setIsVisibleModal={setIsVisibleModal}
            handleRefreshChange={handleRefreshChange}
          />
        )}
      </Modal>
    </>
  );
}
